import { Component, Input, OnInit } from '@angular/core';
import { InputNumberModule } from 'primeng/inputnumber';

import { PaginatorModule } from 'primeng/paginator';
import { AnswerOption, QuestionBoxComponent } from '@shared/components/question-box/question-box.component';
import { TranslocoPipe } from '@jsverse/transloco';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { takeUntil } from 'rxjs';
import { BvsHaftpflichtUndKaskoFormModel } from '../../models/form/bvs-haftpflicht-und-kasko-form.model';
import { BoatDataBaseComponent } from '@shared/directives/boat-data-base.component';
import { countryRegisteredAnswerOptions } from '@shared/models/country-registered-answer-options';
import { defaultAnswerOptions } from '@shared/models/default-answer-options';
import {
  BoatType,
  CallidusBoatCountryRegistered,
  BoatHullMaterialOptions,
  BoatSailingArea,
  BoatSailingAreaTranslocoMap,
  CallidusBvsVersicherungssummeKunde
} from '@backend/models';
import { EnumLabelKeysModel } from '@shared/models/enum-label-keys.model';

@Component({
  selector: 'app-boat-data-haftpflicht-kasko',
  imports: [
    InputNumberModule,
    PaginatorModule,
    QuestionBoxComponent,
    TranslocoPipe,
    ReactiveFormsModule
  ],
  templateUrl: './boat-data-haftpflicht-kasko.component.html',
  styleUrl: './boat-data-haftpflicht-kasko.component.scss'
})
export class BoatDataHaftpflichtKaskoComponent extends BoatDataBaseComponent implements OnInit {
  @Input() form!: FormGroup<BvsHaftpflichtUndKaskoFormModel>;

  hullMaterialValues = BoatHullMaterialOptions.map(opt => opt);

  countryRegisteredAnswerOptions = countryRegisteredAnswerOptions;

  sailingAreaAnswerOptions: AnswerOption<BoatSailingArea>[] = [
    {
      title: BoatSailingAreaTranslocoMap[BoatSailingArea.Binnensee],
      id: BoatSailingArea.Binnensee,
      value: BoatSailingArea.Binnensee
    },
    {
      title: BoatSailingAreaTranslocoMap[BoatSailingArea.MittelmeerNordUndOstsee],
      id: BoatSailingArea.MittelmeerNordUndOstsee,
      value: BoatSailingArea.MittelmeerNordUndOstsee
    }
  ];
  protected readonly defaultAnswerOptions = defaultAnswerOptions;

  ngOnInit(): void {
    const infoCtrls = this.form.controls.bvsBoatInformation.controls;

    infoCtrls
      .countryRegistered
      .valueChanges
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        /**
         * wenn der User DE oder AT als CountryRegistered auswählt hat er die Wahl,
         * ob das Boot nur in Binnengewässern oder auch im Mittelmeer segelt
         *
         * daher können wir, wenn das Land nicht AT oder DE ist, sailingArea immer auf Mittelmeer setzen,
         * da die anderen Länder alle am Mittelmeer liegen
         */
        if (!this.canShowSailingArea()) {
          infoCtrls.sailingArea.setValue(BoatSailingArea.MittelmeerNordUndOstsee);
        }
      });

    // wenn die SailingArea Mittelmeer ist, soll der SB in der Haftpflicht per default höher sein
    infoCtrls
      .sailingArea
      .valueChanges
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(sailingArea => {
        if (sailingArea === BoatSailingArea.MittelmeerNordUndOstsee) {
          this.form.controls.bvsFigures.controls.callidusBvsVersicherungssummeKunde.setValue(CallidusBvsVersicherungssummeKunde.Versicherungssumme10Mio);
        }
      });

  }

  canShowConstructionType(): boolean {
    const infoCtrls = this.form.controls.bvsBoatInformation.controls;
    const isMotorbootMotoryacht = infoCtrls.boatType.value === BoatType.MotorbootMotoryacht;
    const hasMaxKnots = infoCtrls.maxKnotsOver40.value !== null && infoCtrls.maxKnotsOver40.valid;
    return isMotorbootMotoryacht && hasMaxKnots;
  }

  canShowHasDinghy(): boolean {
    const infoCtrls = this.form.controls.bvsBoatInformation.controls;

    if (infoCtrls.boatType.value === BoatType.MotorbootMotoryacht) {
      return infoCtrls.boatConstructionType.value !== null;
    } else if (infoCtrls.boatType.value === BoatType.SegelbootSegelyacht) {
      return infoCtrls.constructionYear.value !== null;
    }

    return false;
  }

  canShowHasModifications(): boolean {
    return this.form.controls.bvsBoatInformation.controls.isBeibootVorhanden.value !== null;
  }

  canShowBoatMaterialDetails(): boolean {
    const infoCtrls = this.form.controls.bvsBoatInformation.controls;
    if (infoCtrls.isBoatModified.value === null || infoCtrls.isBoatModified.value === undefined) {
      return false;
    }

    return infoCtrls.isBoatModified.value && infoCtrls.boatModifiedValue.value !== null || !infoCtrls.isBoatModified.value;
  }

  canShowSailingArea(): boolean {
    const infoCtrls = this.form.controls.bvsBoatInformation.controls;

    if (infoCtrls.countryRegistered.value === null) {
      return false;
    }

    return infoCtrls.countryRegistered.value === CallidusBoatCountryRegistered.Aut
      || infoCtrls.countryRegistered.value === CallidusBoatCountryRegistered.Ger;
  }

  canNavigateNextForm(): boolean {
    const boatInformationCtrls = this.form.controls.bvsBoatInformation.controls;

    const hasModelAndYard = !!boatInformationCtrls.boatModel.value && !!boatInformationCtrls.boatYard.value;

    if (!hasModelAndYard) {
      return false;
    }

    if (boatInformationCtrls.boatType.value === BoatType.SegelbootSegelyacht) {
      return this.canNavigateNextForSailBoat();
    }

    return true;
  }

  private canNavigateNextForSailBoat(): boolean {
    const boatInformationCtrls = this.form.controls.bvsBoatInformation.controls;
    return !!boatInformationCtrls.mastMaterial.value && !!boatInformationCtrls.sailSurfaceInMetersSquared.value;
  }

  isNotGermanyOrAustriaOrNull(): boolean {
    const infoCtrls = this.form.controls.bvsBoatInformation.controls;
    return infoCtrls.countryRegistered.value !== CallidusBoatCountryRegistered.Aut
      && infoCtrls.countryRegistered.value !== CallidusBoatCountryRegistered.Ger
      && infoCtrls.countryRegistered.value !== null;
  }

  prevClicked(): void {
    this.prev.emit();
  }

  nextClicked(): void {
    this.next.emit();
  }

  protected readonly BoatType = BoatType;
}

export enum SailingAndEngineOptionEnum {
  OnlyEngine = 'OnlyEngine',
  OnlySail = 'OnlySail',
  SailAndEngine = 'SailAndEngine',
  Nothing = 'Nothing'
}

export namespace SailingAndEngineOptionEnumValues {
  export function getLabelKeys(): EnumLabelKeysModel<SailingAndEngineOptionEnum>[] {
    return [
      { key: SailingAndEngineOptionEnum.OnlyEngine, label: 'bvs.calculator.onlyEngine' },
      { key: SailingAndEngineOptionEnum.OnlySail, label: 'bvs.calculator.onlySail' },
      { key: SailingAndEngineOptionEnum.SailAndEngine, label: 'bvs.calculator.sailAndEngine' },
      { key: SailingAndEngineOptionEnum.Nothing, label: 'bvs.calculator.nothing' }
    ];
  }

  export function getEnumByKey(key: SailingAndEngineOptionEnum): EnumLabelKeysModel<SailingAndEngineOptionEnum> | undefined {
    return getLabelKeys().find(k => k.key === key);
  }
}
